import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  CFDetailHero,
  ContributorPattern,
  RecipientContext,
  CFRecordModal,
} from '../components/campaign-finance';
import type { Contribution } from '../types/campaignFinance';
import { getContributorDisplayName } from '../utils/campaignFinanceUtils';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

const CampaignFinanceDetailPage: React.FC = () => {
  usePageTitle('Contribution Detail', 'Campaign finance contribution record');

  const { id } = useParams<{ id: string }>();
  const [contribution, setContribution] = useState<Contribution | null>(null);
  const [relatedContributions, setRelatedContributions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [modalContribution, setModalContribution] = useState<Contribution | null>(null);

  const handleViewRecord = useCallback(async (recordId: string) => {
    if (recordId === id && contribution) {
      setModalContribution(contribution);
      setRecordOpen(true);
      return;
    }
    try {
      const res = await api.get(`/campaign-finance/${recordId}`);
      if (res.data.success) {
        setModalContribution(res.data.data.contribution);
        setRecordOpen(true);
      }
    } catch {
      setModalContribution(null);
    }
  }, [id, contribution]);

  useEffect(() => {
    if (id) fetchContribution(id);
  }, [id]);

  const fetchContribution = async (contributionId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/campaign-finance/${contributionId}`);
      if (response.data.success) {
        setContribution(response.data.data.contribution);
        setRelatedContributions(response.data.data.related_contributions || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch contribution details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !contribution) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="card border-error-200 dark:border-error-800 bg-error-50 dark:bg-error-900/20 text-center">
          <p className="text-base text-error-800 dark:text-error-200">{error || 'Contribution not found'}</p>
          <Link to="/campaign-finance" className="text-sm text-primary-600 dark:text-primary-dark-400 hover:underline mt-2 inline-block">
            Back to Campaign Finance
          </Link>
        </div>
      </div>
    );
  }

  const contributorName = getContributorDisplayName(contribution);

  const contributorGifts = relatedContributions.map((r: any) => ({
    id: r.id,
    date: r.contribution_date,
    recipient: r.committee_name || 'Unknown',
    recipient_co_id: r.co_id || null,
    recipient_party: r.candidate_party || null,
    amount: Number(r.contribution_amount) || 0,
    type: r.contribution_type,
    current: r.id === id,
  }));

  const recipientRecent = relatedContributions
    .filter((r: any) => r.committee_name === contribution.committee_name)
    .map((r: any) => ({
      id: r.id,
      date: r.contribution_date,
      contributor: r.computed_contributor_name || 'Anonymous',
      city: r.city,
      amount: Number(r.contribution_amount) || 0,
      type: r.contribution_type,
      current: r.id === id,
    }));

  return (
    <div className="space-y-7 pt-2">
      {/* Hero */}
      <CFDetailHero
        contribution={contribution}
        onViewRaw={() => { setModalContribution(contribution); setRecordOpen(true); }}
      />

      {/* Context sections */}
      {contributorGifts.length > 1 && (
        <ContributorPattern
          contributor={contributorName}
          gifts={contributorGifts}
          currentContributionId={id}
          onViewRecord={handleViewRecord}
        />
      )}

      {recipientRecent.length > 1 && (
        <RecipientContext
          committee={contribution.committee_name || 'Unknown Committee'}
          committeeCoId={contribution.committee_name ? contribution.co_id : null}
          recent={recipientRecent}
          currentContributionId={id}
        />
      )}

      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
          Source: Colorado Secretary of State campaign finance filings · Capitol Commons
        </p>
      </div>

      {/* Record modal */}
      {modalContribution && (
        <CFRecordModal
          contribution={modalContribution}
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
        />
      )}
    </div>
  );
};

export default CampaignFinanceDetailPage;
